import { Form, Header, Message, Segment } from '@mairistem/composants';
import { Combobox } from '@mairistem/finances-utils';
import React from 'react';
import ReactDropzone from 'react-dropzone';
import userRole from '../../../../constants/UserRole';
import UtilisateurError from '../../../../enums/UtilisateurError';
import type Collectivite from '../../../../interfaces/collectivite';
import type { UtilisateurCollectiviteToCreate } from '../../../../interfaces/utilisateurCollectivite';

interface PTeletransmissionStepProps {
	entitesPastell: Collectivite[];
	fieldsOnError: UtilisateurError[];
	certificatFile?: File;
	handleChangeCertificatFile: (files: File[]) => void;
	newUser: Partial<UtilisateurCollectiviteToCreate>;
	setNewUser: (user: Partial<UtilisateurCollectiviteToCreate>) => void;
}

const TeletransmissionStep = ({
	entitesPastell,
	fieldsOnError,
	certificatFile,
	handleChangeCertificatFile,
	setNewUser,
	newUser,
}: PTeletransmissionStepProps) => {
	const handleChangeSlowCollectivites = (
		_e: React.SyntheticEvent<HTMLElement>,
		{ value }: { value: string[] },
	) => {
		// value contient les id_e des collectivités sélectionnées
		let hasSlow = false;
		const updatedCollectivites = entitesPastell?.map((collectivite) => {
			// Si une des collectivités autre que celle sélectionnée possède déjà une collectiviteSlowId
			if (
				collectivite.collectiviteSlowId &&
				!value.includes(collectivite.id_e)
			) {
				hasSlow = true;
			}
			// Si la collectivité est sélectionnée dans le combobox
			if (value.includes(collectivite.id_e)) {
				// Trouver la collectivité correspondante dans entitesPastell pour récupérer les infos SLOW
				const collectivitePastell = entitesPastell.find(
					(entite: Collectivite) => entite.id_e === collectivite.id_e,
				);
				hasSlow = true;
				return {
					...collectivite,
					collectiviteSlowId: collectivitePastell?.collectiviteSlowId,
					collectiviteSlowName: collectivitePastell?.collectiviteSlowName,
					roleSlow: userRole,
				};
			}

			// Si la collectivité n'est pas sélectionnée, on retire les propriétés SLOW
			const { collectiviteSlowId, collectiviteSlowName, roleSlow, ...rest } =
				collectivite;

			return rest;
		});

		setNewUser({
			hasSlow,
			...newUser,
			collectivites: updatedCollectivites,
		});
	};

	const collectivitesOptions = React.useMemo(
		() =>
			entitesPastell.reduce<
				Array<{
					key: string;
					text: string;
					value: string;
					'data-testid': string;
				}>
			>((acc, entite) => {
				if (entite.collectiviteSlowId) {
					acc.push({
						key: entite.libelle,
						text: entite.libelle,
						value: entite.id_e,
						'data-testid': `bordereauAction-${entite.libelle}`,
					});
				}
				return acc;
			}, []),
		[entitesPastell],
	);

	return (
		<Segment basic>
			<Header as="h4" textAlign="left">
				Entité Pastell liées au code client
			</Header>
			{fieldsOnError.includes(UtilisateurError.COLLECTIVITE_SLOW) && (
				<Message error>Veuillez sélectionner une collectivité SLOW</Message>
			)}
			<Form.Field
				control={Combobox}
				// @ts-expect-error
				selection
				options={collectivitesOptions}
				fluid
				multiple
				onChange={handleChangeSlowCollectivites}
				placeholder="Sélectionner une ou plusieurs entités"
				value={newUser?.collectivites?.map((composant) => composant?.id_e)}
			/>
			<Header as="h4" textAlign="left">
				Certificat RGS 2* (obligatoire)
			</Header>
			{fieldsOnError.includes(UtilisateurError.CERTIFICAT) && (
				<Message error>Veuillez renseigner un certificat</Message>
			)}
			<ReactDropzone
				accept={{
					'application/pkix-cert': [],
					'application/x-x509-ca-cert': [],
					'.cer': [],
				}}
				onDrop={handleChangeCertificatFile}
				multiple={false}
			>
				{({ getRootProps, getInputProps }) => (
					<div {...getRootProps()}>
						<Segment>
							<input {...getInputProps()} />
							{certificatFile ? (
								<p>Fichier: {certificatFile.name}</p>
							) : (
								<p>Choisir un fichier: Aucun fichier choisi</p>
							)}
						</Segment>
					</div>
				)}
			</ReactDropzone>
			<p style={{ color: 'grey', paddingTop: '10px' }}>
				Le certificat sera vérifié automatiquement pour s&apos;assurer
				qu&apos;il respecte les normes RGS 2*
			</p>
		</Segment>
	);
};

export default TeletransmissionStep;
